import EditItemDialog from 'components/util/EditItemDialog';
import { Form, FormikProvider, getIn, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { CampaignSummary } from '../../../@types/campaign';
import { addIvrMap, getIvrMap, getIvrMaps, updateIvrMap } from '../../../redux/slices/organization';
import { RootState, useSelector } from '../../../redux/store';
import RjsfForm from '../../../components/rjsf/RjsfForm';
import { IvrMap, IVRType } from '../../../@types/ivr';
import { setIvrToTest } from 'redux/slices/ivr';
import useCustomDispatch, { useCustomDispatchWithoutPromise } from 'redux/dispatch';
import { Button, TextField } from '@nps.today/component-library';
import { useRegisterUnsavedFormChanges } from 'components/form/useRegisterUnsavedFormChanges';
import { getIvrTestUrl } from '../util/getIvrTestUrl';
import { CustomAutocomplete } from 'components/util/CustomAutocomplete';
import { useTranslator } from 'translation/useTranslator';

const componentDataTestId = 'ivr-dialog';

export type IVRDialogProps = {
  campaigns: CampaignSummary[];
  open: boolean;
  onClose: any;
  ivrIdToEdit: number | null;
};

export default function IVRDialog({ open, onClose, campaigns, ivrIdToEdit }: IVRDialogProps) {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const customDispatchWithoutPromise = useCustomDispatchWithoutPromise();
  const [rjsfSubmitCount, setRjsfSubmitCount] = useState(0);
  const { ivrTypes } = useSelector((state: RootState) => state.ivr);
  const { loading, ivrMap, ivrMaps } = useSelector((state: RootState) => state.organization);
  const isLoading = loading.ivrMap;

  useEffect(() => {
    customDispatch({
      action: getIvrMap,
      actionParameters: ivrIdToEdit,
      disableSuccessMessage: true
    });
  }, [customDispatch, ivrIdToEdit]);

  const validationShape = {
    name: Yup.string().required(translator.nameRequired()),
    campaignId: Yup.number().required(translator.campaignRequired()),
    ivrId: Yup.number().required(translator.ivrRequired()),
    isQueueIdRequired: Yup.boolean(),
    queueId: Yup.string()
      .when('isQueueIdRequired', {
        is: true,
        then: Yup.string().required(translator.queueIdRequired())
      })
      .test('unique-ivr-queue', translator.duplicateIvrConnection(), function (queueId) {
        const { ivrId } = this.parent;
        if (!ivrId || !queueId) {
          return true;
        }
        return !ivrMaps?.some(
          (existing) =>
            existing.ivrId === ivrId && existing.queueId === queueId && existing.id !== ivrIdToEdit
        );
      })
  };

  const Schema = Yup.object().shape(validationShape);

  const getEditedIvrObject = (formValues: any) => {
    const ivr = { ...formValues };
    if (rjsfFormData) {
      ivr.properties = rjsfFormData;
    }
    return ivr;
  };

  const formik = useFormik<Partial<IvrMap>>({
    enableReinitialize: true,
    initialValues: {
      id: ivrMap?.id,
      name: ivrMap?.name ?? '',
      campaignId: ivrMap?.campaign?.id ?? null,
      queueId: ivrMap?.queueId ?? '',
      ivrId: ivrMap?.provider?.id ?? null
    },
    validationSchema: Schema,
    onSubmit: async (values, { setSubmitting }) => {
      const ivr = getEditedIvrObject(values);
      if (ivrIdToEdit) {
        customDispatch({
          action: updateIvrMap,
          actionParameters: ivr,
          onSuccess: () => {
            resetForm();
            closeDialog();
            customDispatch({ action: getIvrMaps, disableSuccessMessage: true });
          },
          onFinally: () => {
            setSubmitting(false);
          }
        });
      } else {
        customDispatch({
          action: addIvrMap,
          actionParameters: ivr,
          onSuccess: () => {
            resetForm();
            closeDialog();
            customDispatch({ action: getIvrMaps, disableSuccessMessage: true });
          },
          onFinally: () => {
            setSubmitting(false);
          }
        });
      }
    }
  });
  const {
    values,
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    getFieldProps,
    resetForm,
    submitForm
  } = formik;

  const onSubmit = () => {
    setRjsfSubmitCount((prevState: number) => prevState + 1);
    return Promise.resolve();
  };

  const [rjsfFormData, setRjsfFormData] = useState<any>(ivrMap?.properties);

  useEffect(() => {
    setRjsfFormData(ivrMap?.properties);
  }, [ivrMap?.properties]);

  useEffect(() => {
    if (!ivrMap) {
      return;
    }
    if (!values.ivrId) {
      return;
    }
    if (values.ivrId !== ivrMap?.provider?.id) {
      setRjsfFormData({});
    }
  }, [values.ivrId, ivrMap]);

  useEffect(() => {
    if (ivrIdToEdit) {
      return;
    }
    if (!values.ivrId) {
      return;
    }
    if (values.ivrId !== ivrMap?.provider?.id) {
      setRjsfFormData({});
    }
  }, [values.ivrId, ivrIdToEdit, ivrMap?.provider?.id]);

  const closeDialog = () => {
    onClose();
    setRjsfFormData({});
    setRjsfSubmitCount(0);
    resetForm();
  };

  const rjsfSchema = ivrTypes?.find((ivrType: IVRType) => ivrType?.id === values.ivrId)?.properties;
  const isRjsfDirty =
    rjsfSchema && JSON.stringify(ivrMap?.properties) !== JSON.stringify(rjsfFormData);

  //queueId is required, if the IVR provider does not have any properties
  useEffect(() => {
    setFieldValue('isQueueIdRequired', !rjsfSchema);
  }, [values.ivrId, setFieldValue, rjsfSchema]);

  useEffect(() => {
    //first render
    if (rjsfSubmitCount <= 0) return;
    //if no schema avil. submit form without rjsf data
    if (!rjsfSchema) {
      submitForm();
    }
  }, [rjsfSubmitCount, submitForm, rjsfSchema]);

  const isFormDirty = () => {
    const currrentValues = values;

    let isFormDirty = false;

    if (ivrMap) {
      const initialValues = {
        campaignId: ivrMap?.campaign?.id,
        id: ivrMap?.id,
        ivrId: ivrMap?.provider?.id,
        name: ivrMap?.name,
        queueId: ivrMap?.queueId
      };
      isFormDirty =
        currrentValues.campaignId !== initialValues.campaignId ||
        currrentValues.ivrId !== initialValues.ivrId ||
        currrentValues.name !== initialValues.name ||
        currrentValues.queueId !== initialValues.queueId;
    } else {
      isFormDirty = Boolean(
        currrentValues.campaignId ||
          currrentValues.ivrId ||
          currrentValues.name ||
          currrentValues.queueId
      );
    }

    //React Json Schema Fields
    return Boolean(isFormDirty || isRjsfDirty);
  };

  useRegisterUnsavedFormChanges(isFormDirty());

  const onClickTest = () => {
    const ivrToTest = getEditedIvrObject(values);

    //the test endpoint only needs the properties, and the ivrId, wrapped as below
    customDispatchWithoutPromise({
      action: setIvrToTest,
      actionParameters: {
        provider: { id: ivrToTest?.ivrId },
        properties: { ...ivrToTest.properties }
      },
      disableSuccessMessage: true
    });
  };

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" onSubmit={handleSubmit}>
        <EditItemDialog
          itemType={translator.ivrConnection()}
          isLoading={isLoading}
          extraMenuButtons={[
            <div key={0}>
              {getIvrTestUrl({ ivrId: Number(values.ivrId), ivrTypes }) && (
                <Button dataTestId={`${componentDataTestId}-test`} onClick={onClickTest}>
                  {translator.test()}
                </Button>
              )}
            </div>
          ]}
          editable
          isOpen={open}
          title={ivrIdToEdit ? translator.updateIvr() : translator.addIvr()}
          editContent={[
            <TextField
              dataTestId={`${componentDataTestId}-name`}
              key={1}
              fullWidth
              label={translator.name()}
              {...getFieldProps('name')}
              error={Boolean(getIn(errors, 'name') && getIn(touched, 'name'))}
              helperText={getIn(touched, 'name') && getIn(errors, 'name')}
            />,
            <CustomAutocomplete
              dataTestId={`${componentDataTestId}-provider`}
              key={2}
              {...getFieldProps('ivrId')}
              onChange={(_, val) => {
                setFieldValue('ivrId', val?.id ?? '');
              }}
              getOptionLabel={(ivr: any) =>
                ivrTypes?.find((x) => x.id === ivr || x.id === ivr.id)?.name ?? ''
              }
              isOptionEqualToValue={(option, value) =>
                option.id === value || option.id === value?.id
              }
              options={ivrTypes}
              renderInput={(params) => (
                <TextField
                  {...params}
                  dataTestId={`${componentDataTestId}-provider`}
                  label={translator.ivrProvider()}
                  error={Boolean(getIn(errors, 'ivrId') && getIn(touched, 'ivrId'))}
                  helperText={getIn(touched, 'ivrId') && getIn(errors, 'ivrId')}
                />
              )}
            />,
            <CustomAutocomplete
              dataTestId={`${componentDataTestId}-campaign-id`}
              key={3}
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.id}>
                    {option.name}
                  </li>
                );
              }}
              {...getFieldProps('campaignId')}
              onChange={(_, val) => {
                setFieldValue('campaignId', val?.id ?? '');
              }}
              getOptionLabel={(ivr: any) =>
                campaigns.find((x) => x.id === ivr || x.id === ivr.id)?.name ?? ''
              }
              isOptionEqualToValue={(option, value) =>
                option.id === value || option.id === value?.id
              }
              options={campaigns}
              renderInput={(params) => (
                <TextField
                  {...params}
                  dataTestId={`${componentDataTestId}-campaign-id`}
                  label={translator.campaign()}
                  error={Boolean(getIn(errors, 'campaignId') && getIn(touched, 'campaignId'))}
                  helperText={getIn(touched, 'campaignId') && getIn(errors, 'campaignId')}
                />
              )}
            />,
            <TextField
              dataTestId={`${componentDataTestId}-queue-id`}
              key={4}
              fullWidth
              label={translator.queueId()}
              {...getFieldProps('queueId')}
              error={Boolean(getIn(errors, 'queueId') && getIn(touched, 'queueId'))}
              helperText={getIn(touched, 'queueId') && getIn(errors, 'queueId')}
            />,
            <div key={5}>
              <RjsfForm
                formData={rjsfFormData}
                setRjsfFormData={setRjsfFormData}
                submitCount={rjsfSubmitCount}
                schema={rjsfSchema}
              />
            </div>
          ]}
          displayContent={[]}
          dirty={isFormDirty()}
          isSubmitting={isSubmitting}
          close={closeDialog}
          resetForm={resetForm}
          submitForm={onSubmit}
        />
      </Form>
    </FormikProvider>
  );
}
